import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { api } from '../api/client';
import {
  Search,
  FlaskConical,
  Package,
  Users,
  FileText,
  ShoppingCart,
  Factory,
  ArrowRight,
  Loader2,
  X,
  Command,
} from 'lucide-react';

interface SearchModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface SearchResult {
  id: string;
  type: 'formula' | 'material' | 'customer' | 'quote' | 'order' | 'production';
  title: string;
  subtitle?: string;
  href: string;
}

const ICONS = {
  formula: FlaskConical,
  material: Package,
  customer: Users,
  quote: FileText,
  order: ShoppingCart,
  production: Factory,
};

const TYPE_LABELS = {
  formula: 'Formula',
  material: 'Raw Material',
  customer: 'Customer',
  quote: 'Quote',
  order: 'Order',
  production: 'Production',
};

const TYPE_COLORS = {
  formula: 'bg-amber-50 text-amber-700 dark:bg-blue-900/30 dark:text-blue-400',
  material: 'bg-amber-50 text-amber-700 dark:bg-green-900/30 dark:text-green-400',
  customer: 'bg-amber-50 text-amber-700 dark:bg-purple-900/30 dark:text-purple-400',
  quote: 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  order: 'bg-amber-50 text-amber-700 dark:bg-indigo-900/30 dark:text-indigo-400',
  production: 'bg-amber-50 text-amber-700 dark:bg-orange-900/30 dark:text-orange-400',
};

export default function SearchModal({ isOpen, onClose }: SearchModalProps) {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const [search, setSearch] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Search queries
  const { data: formulas, isLoading: formulasLoading } = useQuery({
    queryKey: ['search', 'formulas', search],
    queryFn: () => api.getFormulas({ search, pageSize: 5 }),
    enabled: isOpen && search.length >= 2,
  });

  const { data: materials, isLoading: materialsLoading } = useQuery({
    queryKey: ['search', 'materials', search],
    queryFn: () => api.getRawMaterials({ search, pageSize: 5 }),
    enabled: isOpen && search.length >= 2,
  });

  const { data: customers, isLoading: customersLoading } = useQuery({
    queryKey: ['search', 'customers', search],
    queryFn: () => api.getCustomers({ search, pageSize: 5 }),
    enabled: isOpen && search.length >= 2,
  });

  const isLoading = formulasLoading || materialsLoading || customersLoading;

  // Build unified results
  const results: SearchResult[] = [];

  if (formulas?.data) {
    formulas.data.forEach((f: any) => {
      results.push({
        id: f.id,
        type: 'formula',
        title: f.code,
        subtitle: f.name,
        href: `/app/formulas/${f.id}`,
      });
    });
  }

  if (materials?.data) {
    materials.data.forEach((m: any) => {
      results.push({
        id: m.id,
        type: 'material',
        title: m.code,
        subtitle: m.name,
        href: `/app/raw-materials/${m.id}`,
      });
    });
  }

  if (customers?.data) {
    customers.data.forEach((c: any) => {
      results.push({
        id: c.id,
        type: 'customer',
        title: c.name,
        subtitle: c.code,
        href: `/app/customers/${c.id}`,
      });
    });
  }

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setSearch('');
      setSelectedIndex(0);
      const timer = setTimeout(() => inputRef.current?.focus(), 100);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Reset selected index when results change
  useEffect(() => {
    setSelectedIndex(0);
  }, [results.length]);

  // Focus trapping within the modal
  useEffect(() => {
    if (!isOpen) return;

    const handleFocusTrap = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const modal = modalRef.current;
      if (!modal) return;

      const focusableElements = modal.querySelectorAll<HTMLElement>(
        'input, button, [href], [tabindex]:not([tabindex="-1"])'
      );
      if (focusableElements.length === 0) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    };

    document.addEventListener('keydown', handleFocusTrap);
    return () => document.removeEventListener('keydown', handleFocusTrap);
  }, [isOpen]);

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((prev) => (prev + 1) % Math.max(1, results.length));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((prev) => (prev - 1 + results.length) % Math.max(1, results.length));
      } else if (e.key === 'Enter' && results[selectedIndex]) {
        e.preventDefault();
        navigate(results[selectedIndex].href);
        onClose();
      } else if (e.key === 'Escape') {
        onClose();
      }
    },
    [results, selectedIndex, navigate, onClose]
  );

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-start justify-center pt-[15vh] px-4"
        onClick={onClose}
      >
        {/* Backdrop */}
        <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />

        {/* Modal */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: -20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: -20 }}
          transition={{ type: 'spring', duration: 0.3 }}
          ref={modalRef}
          className="relative w-full max-w-2xl bg-white/80 dark:bg-sky-900/30 rounded-2xl shadow-2xl overflow-hidden"
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-label="Search"
        >
          {/* Search Input */}
          <div className="relative border-b border-gray-200 dark:border-sky-700/40">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 dark:text-gray-500" />
            <input
              ref={inputRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Search formulas, materials, customers..."
              className="w-full py-4 pl-12 pr-12 text-lg bg-transparent border-0 focus:outline-none focus:ring-0 text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-gray-500"
              autoComplete="off"
            />
            <button
              onClick={onClose}
              className="absolute right-4 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600 dark:hover:text-gray-300 rounded"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Results */}
          <div className="max-h-[400px] overflow-y-auto">
            {search.length < 2 ? (
              <div className="p-8 text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-slate-100 dark:bg-sky-900/50 mb-4">
                  <Search className="w-6 h-6 text-slate-400 dark:text-gray-500" />
                </div>
                <p className="text-slate-400 dark:text-gray-400">Type at least 2 characters to search</p>
                <div className="flex items-center justify-center gap-4 mt-6 text-sm text-slate-500 dark:text-gray-500">
                  <span className="flex items-center gap-1">
                    <kbd className="px-2 py-1 bg-slate-100 dark:bg-sky-900/50 rounded text-xs font-medium text-slate-700 dark:text-gray-300">↑↓</kbd>
                    Navigate
                  </span>
                  <span className="flex items-center gap-1">
                    <kbd className="px-2 py-1 bg-slate-100 dark:bg-sky-900/50 rounded text-xs font-medium text-slate-700 dark:text-gray-300">↵</kbd>
                    Select
                  </span>
                  <span className="flex items-center gap-1">
                    <kbd className="px-2 py-1 bg-slate-100 dark:bg-sky-900/50 rounded text-xs font-medium text-slate-700 dark:text-gray-300">esc</kbd>
                    Close
                  </span>
                </div>
              </div>
            ) : isLoading ? (
              <div className="p-8 text-center">
                <Loader2 className="w-8 h-8 animate-spin text-slate-400 dark:text-gray-500 mx-auto" />
                <p className="text-slate-400 dark:text-gray-400 mt-2">Searching...</p>
              </div>
            ) : results.length === 0 ? (
              <div className="p-8 text-center">
                <p className="text-slate-400 dark:text-gray-400">No results found for "{search}"</p>
              </div>
            ) : (
              <div className="py-2">
                {results.map((result, index) => {
                  const Icon = ICONS[result.type];
                  return (
                    <button
                      key={`${result.type}-${result.id}`}
                      onClick={() => {
                        navigate(result.href);
                        onClose();
                      }}
                      className={`w-full flex items-center gap-4 px-4 py-3 text-left transition-colors ${
                        index === selectedIndex ? 'bg-orange-50 dark:bg-orange-900/20' : 'hover:bg-slate-50 dark:hover:bg-sky-800/50'
                      }`}
                    >
                      <div className={`flex items-center justify-center w-10 h-10 rounded-xl ${TYPE_COLORS[result.type]}`}>
                        <Icon className="w-5 h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-slate-900 dark:text-white truncate">{result.title}</div>
                        {result.subtitle && (
                          <div className="text-sm text-slate-400 dark:text-gray-400 truncate">{result.subtitle}</div>
                        )}
                      </div>
                      <span className="text-xs px-2 py-1 rounded-full bg-slate-100 dark:bg-sky-900/50 text-slate-600 dark:text-gray-300">
                        {TYPE_LABELS[result.type]}
                      </span>
                      {index === selectedIndex && (
                        <ArrowRight className="w-4 h-4 text-orange-500" />
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-4 py-3 bg-slate-50 dark:bg-gray-900 border-t border-gray-200 dark:border-sky-700/40 flex items-center justify-between text-xs text-slate-400 dark:text-gray-400">
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1">
                <FlaskConical className="w-3.5 h-3.5" />
                Formulas
              </span>
              <span className="flex items-center gap-1">
                <Package className="w-3.5 h-3.5" />
                Materials
              </span>
              <span className="flex items-center gap-1">
                <Users className="w-3.5 h-3.5" />
                Customers
              </span>
            </div>
            <div className="flex items-center gap-1">
              <Command className="w-3.5 h-3.5" />
              <span>K to toggle</span>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
